/**
 * Data tables.
 *
 * Not virtualised, deliberately (§13). Tables here are read by scanning, and
 * a virtualised table breaks ctrl-F -- which is how people actually search
 * one. A 400-row statement renders whole; the browser is fine with it.
 */
import type { HTMLAttributes, ThHTMLAttributes, TdHTMLAttributes } from 'react'
import { cn } from '~/lib/cn.ts'

export function Table({ className, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return (
    // `overscroll-x-contain` so swiping a wide table sideways on a phone
    // does not chain into the browser's back gesture at the end of it.
    <div className="scroll-x-hint overflow-x-auto overscroll-x-contain">
      <table
        className={cn('w-full border-collapse text-[length:var(--text-data)]', className)}
        {...rest}
      />
    </div>
  )
}

export function Th({
  className, numeric, ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      className={cn(
        'border-b border-[var(--border)] bg-[var(--bg-sunken)] px-2.5 py-2',
        'text-[length:var(--text-micro)] font-semibold uppercase tracking-[0.05em]',
        'text-[var(--text-faint)] whitespace-nowrap',
        numeric ? 'text-end' : 'text-start',
        className,
      )}
      {...rest}
    />
  )
}

export function Td({
  className, numeric, ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cn(
        'border-b border-[var(--border)] px-2.5 py-1.5 align-top',
        numeric ? 'tnum text-end' : 'text-start',
        className,
      )}
      {...rest}
    />
  )
}

export function Tr({
  className, interactive, ...rest
}: HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return (
    <tr
      className={cn(
        interactive && 'cursor-default hover:bg-[var(--bg-sunken)] active:bg-[var(--bg-sunken)]',
        className,
      )}
      {...rest}
    />
  )
}
